import React from 'react'
import Select from 'react-select'
import axios from 'axios'
import {
  FormInput,
  FormInputDate,
  FormInputText,
  FormInputTextArea,
  Alert,
  FormInputCheckbox
} from 'camino-react'
import {
  map,
  includes,
  get,
  sortBy,
  isEmpty,
  find,
  indexOf,
  isNull
} from 'lodash'

import Col from 'Components/Col'
import Row from 'Components/Row'
import FormSection from 'Components/FormSection'
import ErrorList from 'Components/ErrorList'

// This component exists almost entirely because the useless react-select version
// that requires a wrapper component with state
class EventForm extends React.Component {
  constructor (props) {
    super(props)

    const { event } = this.props

    this.state = {
      ...props,
      loadingTickets: false,
      ticketErrors: [],
      resourceErrors: [],
      assignResource: isNull(get(event, 'ticket.assignee_user_id')),
      event: { ...event, assignResource: isNull(get(event, 'ticket.assignee_user_id')) }
    }
  }

  static getDerivedStateFromProps (nextProps, prevState) {
    if (nextProps.event !== prevState.event) {
      return { event: nextProps.event }
    }

    return null
  }

  componentDidMount () {
    const { event, assignResource } = this.state

    if ((event.meta && event.meta.taskable_id) || event.ticket) {
      this.getSelectedTicket()
      this.assignResource(assignResource)
    }

    this.checkResourceQueue()
  }

  getSelectedTicket () {
    const { ticketsForSelect, event } = this.state
    const ticketId = get(event.meta, 'taskable_id') || get(event.ticket, 'id')
    const ticketIndex = indexOf(ticketsForSelect, find(ticketsForSelect, ['id', ticketId]))

    this.setState({ loadingTickets: true, ticketErrors: [] }, () => {
      axios.get(`/tickets/${ticketId}`, { responseType: 'json' })
        .then(({ data }) => {
          ticketIndex > 0 ? ticketsForSelect[ticketIndex] = data : ticketsForSelect.push(data)

          this.setState({ ticketsForSelect, ticketErrors: data.errors, loadingTickets: false })
        })
    })
  }

  checkResourceQueue () {
    const { resourcesForSelect, event, formSource } = this.state
    let resourceHasQueue = false

    resourceHasQueue = get(find(resourcesForSelect, ['value', event.resourceId]), 'has_queue', false)

    if (!resourceHasQueue) {
      const error = `${formSource === 'Dispatcher' ? 'Resource ' : 'User'} has not been assigned to any queue`
      this.setState({ resourceErrors: [error] })
    } else {
      this.setState({ resourceErrors: [] })
    }
  }

  handleTicketSelect (selectedTicket) {
    const { event, ticketsForSelect } = this.state
    const ticket = find(ticketsForSelect, ['id', get(selectedTicket, 'value')])

    if (ticket) {
      event.title = `Ticket #${ticket.id.value || ticket.id} - ${ticket.title}`
      event.description = ticket.description
      event.ticket = ticket
    }

    this.setState({ event }, () => this.getSelectedTicket())
  }

  assignResource (assign) {
    const { event } = this.state

    event.assignResource = assign
    this.setState({
      assignResource: assign,
      event
    }, () => this.checkResourceQueue())
  }

  render () {
    const {
      currentUser,
      loadingTickets,
      onDeleteEvent,
      onSubmitEvent,
      resourcesForSelect,
      ticketsForSelect,
      ticketErrors,
      resourceErrors,
      event,
      assignResource,
      formSource
    } = this.state

    const ticketsOptions = map(ticketsForSelect, ticket => (
      {
        value: ticket.id,
        label: `#${ticket.id.value || ticket.id}${ticket.client_name ? ` (${ticket.client_name})` : ''}: ${ticket.title}`
      }
    ))

    const ticketId = get(event.ticket, 'id') || get(event.meta, 'taskable_id')
    const ticketAssigneeId = get(find(ticketsForSelect, ['id', ticketId]), 'assignee.id')

    const workLocations = [
      { value: 'on_site', label: 'On site' },
      { value: 'remote', label: 'Remote' },
      { value: 'in_house', label: 'In house' }
    ]
    event.createdBy = currentUser

    return (
      <form className="dispatcher__event">
        {(!isEmpty(ticketErrors) || !isEmpty(resourceErrors)) && (
          <>
            <Alert type="warning" showAlert title="Ticket will not be updated due to:">
              <ErrorList errors={ticketErrors.concat(resourceErrors)} />
            </Alert>
            Please fill in required information for Ticket
            <a href={`/tickets/${ticketId}`} target="_blank" rel="noopener noreferrer"> {`#${ticketId}`}</a>
          </>
        )}
        {event.error_messages && (
          <Alert type="error" showAlert>
            <ErrorList errors={event.error_messages} />
          </Alert>
        )}

        <FormSection className="dispatcher__event__details">
          <FormInput label="Title">
            <FormInputText
              id="event_title"
              value={event.title}
              onValue={(text) => {
                event.title = text
                this.setState({ event })
              }}
              className={includes(event.error_fields, 'title') ? 'error' : undefined}
            />
          </FormInput>

          <FormInput label="Description">
            <FormInputTextArea
              id="event_description"
              onValue={(text) => {
                event.description = text
                this.setState({ event })
              }}
              value={get(event, 'description') || get(event, 'meta.notes')}
            />
          </FormInput>

          <div className="row row--bottom-spaced">
            <Col>
              <FormInput label="Start date">
                <FormInputDate
                  enableTime
                  value={event.start}
                  onValue={(date) => {
                    event.start = date
                    this.setState({ event })
                  }}
                />
              </FormInput>
            </Col>
            <Col>
              <FormInput label="End date">
                <FormInputDate
                  enableTime
                  value={event.end}
                  onValue={(date) => {
                    event.end = date
                    this.setState({ event })
                  }}
                />
              </FormInput>
            </Col>
          </div>

          <div className="w-full mb-6">
            <FormInput label={`${formSource === 'Dispatcher' ? 'Resource' : 'Assignee'}`}>
              <Select
                className="mb-0"
                options={sortBy(resourcesForSelect, 'label')}
                value={event.resourceId}
                onChange={(selectedOption) => { event.resourceId = selectedOption.value; this.setState({ event }); this.checkResourceQueue() }}
              />
            </FormInput>

            {ticketId && (
              <FormInputCheckbox
                value={assignResource || event.resourceId === ticketAssigneeId}
                onValue={value => this.assignResource(value)}
                disabled={isNull(ticketId)}
              >
                Assign to Ticket
              </FormInputCheckbox>
            )}
          </div>

          <Row>
            <Col>
              <FormInput label="Work location">
                <Select
                  options={workLocations}
                  value={event.workLocation || _.get(event, 'meta.work_location')}
                  onChange={(selectedOption) => { event.workLocation = selectedOption.value; this.setState({ event }) }}
                />
              </FormInput>
            </Col>
          </Row>

          {formSource === 'Dispatcher' && (
            <FormInput label="Ticket">
              <Select
                options={ticketsOptions}
                value={get(event, 'ticket.id') || get(event, 'meta.taskable_id')}
                onChange={ticket => this.handleTicketSelect(ticket)}
                isLoading={loadingTickets}
              />
            </FormInput>
          )}

          {ticketId && formSource === 'Dispatcher' && (
            <a href={`/tickets/${ticketId}`} target="_blank" rel="noopener noreferrer">View Ticket</a>
          )}

          <div className="form-section__actions form-section__actions--space-between">
            {get(event, 'meta.id') && (
              <button
                className="button button--destructive"
                type="button"
                onClick={(e) => {
                  if (confirm('Are you sure you want to delete this event?')) {
                    onDeleteEvent(event)
                  }
                }}
              >
                Delete
              </button>
            )}

            <button
              type="submit"
              onClick={(e) => { e.preventDefault(); onSubmitEvent(event) }}
              className="form-section__action button"
            >
              {get(event, 'meta.id') ? 'Update' : 'Create'}
            </button>
          </div>
        </FormSection>


      </form>
    )
  }
}

export default EventForm
